/**
 * Trojitá pojistka režimu ceníku výkopů (`linear` m vs. `volume` m³/m²):
 * 1) UI zamkne přepínač v Obnově,
 * 2) billing čte režim z Výkopu (metadata / povrchy),
 * 3) ledger guard na PHASE.2 vynutí režim podle řádků PHASE.1.
 */

import {
  normalizeExcavationPricingMode,
  type ExcavationPricingMode,
} from '../constants/excavation-material.constants';
import {
  WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING,
  WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING,
} from '../constants/work-report-price-list.constants';

export type ExcavationPricingModeEvidence = {
  excavationPricingMode?: unknown;
  excavationSurfaces?: ReadonlyArray<{ surfaceBucket?: string | null } | null> | null;
  obnovaSurfaces?: ReadonlyArray<unknown> | null;
};

export type WorkReportLedgerModeHint = {
  listItemCode?: string | null;
  metadata?: Record<string, unknown> | null;
  phaseTo?: string | null;
};

/** Explicitní režim z snapshotu / metadat (bez org defaultu). */
export function readExplicitExcavationPricingMode(
  raw: unknown,
): ExcavationPricingMode | undefined {
  if (raw === 'volume' || raw === 'linear') return raw;
  if (raw === 1 || raw === '1' || raw === true) return 'volume';
  if (raw === 0 || raw === '0' || raw === false) return 'linear';
  return undefined;
}

/**
 * Odvození režimu z dat Výkopu / Obnovy bez org defaultu.
 * `surfaceBucket` / `obnovaSurfaces` → volume; jinak undefined (ať rozhodne caller).
 */
export function inferExcavationPricingModeFromSurfaces(
  evidence: ExcavationPricingModeEvidence | null | undefined,
): ExcavationPricingMode | undefined {
  if (!evidence) return undefined;
  const explicit = readExplicitExcavationPricingMode(evidence.excavationPricingMode);
  if (explicit) return explicit;
  if (Array.isArray(evidence.obnovaSurfaces) && evidence.obnovaSurfaces.length > 0) {
    return 'volume';
  }
  if (
    evidence.excavationSurfaces?.some((s) => Boolean((s?.surfaceBucket ?? '').toString().trim()))
  ) {
    return 'volume';
  }
  // Klasické povrchy (materiál bez bucketu) samy o sobě neznamenají linear —
  // mohou být legacy volume bez bucketu. Vrátíme undefined.
  return undefined;
}

/** True, když už existuje důkaz o režimu z Výkopu (explicitní mode nebo volume buckety). */
export function hasVykopPricingModeEvidence(
  evidence: ExcavationPricingModeEvidence | null | undefined,
): boolean {
  if (!evidence) return false;
  if (readExplicitExcavationPricingMode(evidence.excavationPricingMode)) return true;
  if (
    evidence.excavationSurfaces?.some((s) => Boolean((s?.surfaceBucket ?? '').toString().trim()))
  ) {
    return true;
  }
  return false;
}

/**
 * Kódy položek, které na PHASE.1 vznikají jen v režimu `volume`
 * (konsolidované m²/m³ + HDPE účtované při Vykopaném).
 */
export function isVolumePhase1WorkReportListItemCode(
  listItemCode: string | null | undefined,
): boolean {
  const code = (listItemCode ?? '').trim();
  if (!code) return false;
  if (code.startsWith('vykop-m3-') || code.startsWith('vykop-m2-')) return true;
  if (code === 'pokladka-hdpe-multiduct') return true;
  return false;
}

/**
 * Inferuje režim z existujících primárních řádků výkazu (typicky PHASE.1).
 * Volume řádky na Vykopaném → `volume`. Explicitní metadata.mode → ten režim.
 * Jinak undefined (linear na PHASE.1 obvykle nic neúčtuje).
 */
export function inferExcavationPricingModeFromWorkReportLedger(
  rows: ReadonlyArray<WorkReportLedgerModeHint> | null | undefined,
): ExcavationPricingMode | undefined {
  if (!rows?.length) return undefined;
  let sawExplicitLinear = false;
  for (const row of rows) {
    const fromMeta = readExplicitExcavationPricingMode(row.metadata?.['excavationPricingMode']);
    if (fromMeta === 'volume') return 'volume';
    if (fromMeta === 'linear') sawExplicitLinear = true;
    if (isVolumePhase1WorkReportListItemCode(row.listItemCode)) {
      return 'volume';
    }
  }
  return sawExplicitLinear ? 'linear' : undefined;
}

export function isVykopRestorationBillingPhase(phaseTo: string | null | undefined): boolean {
  return WORK_REPORT_PHASES_VYKOP_RESTORATION_BILLING.includes((phaseTo ?? '').trim());
}

export function isVykopExcavationBillingPhase(phaseTo: string | null | undefined): boolean {
  return WORK_REPORT_PHASES_VYKOP_EXCAVATION_BILLING.includes((phaseTo ?? '').trim());
}

/** PHASE.2 → odpovídající PHASE.1 billing kód (VYKOP / JAMA). */
export function correspondingVykopExcavationBillingPhase(
  restorationPhaseTo: string | null | undefined,
): string | undefined {
  const to = (restorationPhaseTo ?? '').trim();
  if (to === 'VYKOP.1.PHASE.2') return 'VYKOP.1.PHASE.1';
  if (to === 'JAMA.1.PHASE.2') return 'JAMA.1.PHASE.1';
  return undefined;
}

export type ResolveExcavationPricingModeInput = {
  /** Režim z aktuálního commitu / excavationData. */
  fromCommit?: ExcavationPricingMode;
  /** Režim uložený na objektu při Výkopu. */
  fromMetadata?: ExcavationPricingMode;
  /** Odvozeno z povrchů (bucket / obnovaSurfaces). */
  inferredFromSurfaces?: ExcavationPricingMode;
  /** Režim zjištěný z primárního ledgeru PHASE.1. */
  phase1LedgerMode?: ExcavationPricingMode;
  /** Výchozí režim firmy / uživatele — nikdy nepřepisuje zamčený Výkop. */
  orgDefault?: ExcavationPricingMode;
  /**
   * Obnova / Finální billing: režim musí sedět s Výkopem / PHASE.1 ledgerem,
   * org default se použije jen když chybí jakýkoli důkaz z Výkopu.
   */
  lockToVykop: boolean;
};

export type ResolveExcavationPricingModeResult = {
  mode: ExcavationPricingMode;
  /** True = režim vynucen z PHASE.1 ledgeru (pojistka proti dvojímu účtování). */
  forcedFromLedger: boolean;
  /**
   * True = commit / odvození chtělo jiný režim než zamčený Výkop / ledger.
   * Caller má zalogovat a použít `mode` (ne tichý špatný ceník).
   */
  mismatchedAttempt: boolean;
  /** Zdroj finálního rozhodnutí (pro logy / testy). */
  source: 'ledger' | 'metadata' | 'surfaces' | 'commit' | 'org_default' | 'linear_fallback';
};

/**
 * Jediný resolver režimu ceníku výkopů pro UI i backend.
 * Při `lockToVykop` (Obnova / PHASE.2) má prioritu ledger → metadata → surfaces → commit;
 * org default se použije jen bez důkazu z Výkopu.
 */
export function resolveExcavationPricingModeWithSafeguards(
  input: ResolveExcavationPricingModeInput,
): ResolveExcavationPricingModeResult {
  const attempted = input.fromCommit;

  if (input.lockToVykop && input.phase1LedgerMode) {
    return {
      mode: input.phase1LedgerMode,
      forcedFromLedger: true,
      mismatchedAttempt: attempted != null && attempted !== input.phase1LedgerMode,
      source: 'ledger',
    };
  }

  if (input.lockToVykop && input.fromMetadata) {
    return {
      mode: input.fromMetadata,
      forcedFromLedger: false,
      mismatchedAttempt: attempted != null && attempted !== input.fromMetadata,
      source: 'metadata',
    };
  }

  if (input.lockToVykop && input.inferredFromSurfaces) {
    return {
      mode: input.inferredFromSurfaces,
      forcedFromLedger: false,
      mismatchedAttempt: attempted != null && attempted !== input.inferredFromSurfaces,
      source: 'surfaces',
    };
  }

  if (input.fromCommit) {
    return {
      mode: input.fromCommit,
      forcedFromLedger: false,
      mismatchedAttempt: false,
      source: 'commit',
    };
  }

  if (input.fromMetadata) {
    return {
      mode: input.fromMetadata,
      forcedFromLedger: false,
      mismatchedAttempt: false,
      source: 'metadata',
    };
  }

  if (input.inferredFromSurfaces) {
    return {
      mode: input.inferredFromSurfaces,
      forcedFromLedger: false,
      mismatchedAttempt: false,
      source: 'surfaces',
    };
  }

  if (input.orgDefault === 'volume' || input.orgDefault === 'linear') {
    return {
      mode: input.orgDefault,
      forcedFromLedger: false,
      mismatchedAttempt: false,
      source: 'org_default',
    };
  }

  return {
    mode: 'linear',
    forcedFromLedger: false,
    mismatchedAttempt: false,
    source: 'linear_fallback',
  };
}

/** Normalizace + evidence helpers pro callery, které posílají raw unknown. */
export function resolveExcavationPricingModeFromEvidence(
  evidence: ExcavationPricingModeEvidence | null | undefined,
  orgDefault?: ExcavationPricingMode,
  options?: { lockToVykop?: boolean; phase1LedgerMode?: ExcavationPricingMode },
): ExcavationPricingMode {
  const explicit = readExplicitExcavationPricingMode(evidence?.excavationPricingMode);
  const inferred = inferExcavationPricingModeFromSurfaces({
    ...evidence,
    excavationPricingMode: undefined,
  });
  return resolveExcavationPricingModeWithSafeguards({
    fromCommit: explicit,
    fromMetadata: explicit,
    inferredFromSurfaces: inferred,
    phase1LedgerMode: options?.phase1LedgerMode,
    orgDefault: orgDefault ? normalizeExcavationPricingMode(orgDefault) : undefined,
    lockToVykop: options?.lockToVykop === true,
  }).mode;
}
